import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, CalendarCheck2, ChevronRight, ClipboardCheck, Package, Plus, RotateCcw, ShieldAlert, Truck, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Note } from '@/components/app/Badges';
import { Panel, PanelHeader } from '@/components/app/States';
import { PageHeader } from '@/components/app/PageHeader';
import { ExampleDataNote, StatusPill, TIME_STATUS_TONE } from '@/components/app/DriftUI';
import { DemoProfileSwitcher } from '@/components/app/DemoProfile';
import { ProductionAnalysisPanel } from '@/components/app/ProductionAnalysis';
import { AttentionList, CaseSheetHost, useCaseParam } from '@/components/app/Maskinsaker';
import { DeviationList } from '@/components/app/Avvikssaker';
import { useDemoProfile } from '@/state/profile';
import { TIME_STATUSES, TIME_STATUS_LABELS, byUnitText, hoursText, listConsumption, listDeviations, listEvents, listMachines, listProjects, listTimeEntries } from '@/lib/driftApi';
import { fmtNumber } from '@/domain/numbers';
import { addDays, formatDateShort, formatDateWeekday, osloDateOf, weekdayOf } from '@/domain/time';
import { cn } from '@/lib/utils';

/*
  Oversikt = arbeidsstart. Det som skal gjøres og følges opp kommer først: egne føringer (Min dag), timer til
  godkjenning/returnert, service og maskinfeil, åpne RUH/kvalitetsavvik, prosjektaktivitet og nylig vareforbruk.
  Rekkefølgen tilpasses demoprofilens rolle (kontor/leder ser oppfølging først, medarbeider ser Min dag først) –
  dette er kun visningskontekst, ikke roller eller tilgangsstyring. Produksjonsanalysen ligger sekundært nederst.
*/

const LOOKBACK_DAYS = 35;

function weekStartOf(date) {
  const wd = weekdayOf(date);
  return addDays(date, wd === 0 ? -6 : 1 - wd);
}

/** Visningsmodus fra demoprofilens rolle. Kun rekkefølge og hvilke oppfølgingsrader som vises. */
export function overviewMode(demoRole) {
  if (demoRole === 'kontor' || demoRole === 'leder') return 'kontor';
  if (demoRole === 'bas') return 'bas';
  return 'medarbeider';
}

const MODE_LABEL = { kontor: 'kontor og oppfølging først', bas: 'egne føringer og laget først', medarbeider: 'egne føringer først' };

/* ------------------------------------------------------------------ */
/* Små byggeklosser                                                     */
/* ------------------------------------------------------------------ */

/** Oppfølgingsrad: ikon, etikett, tall og én linje kontekst. Hele raden er en lenke. */
function TodoRow({ icon: Icon, label, value, meta, to, tone, testId }) {
  const zero = !value;
  return (
    <li>
      <Link
        to={to}
        className="group flex min-h-[52px] items-center gap-3 px-4 py-2 transition-colors duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--hr-focus)] sm:px-5"
        data-testid={testId}
      >
        <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px]', zero ? 'bg-[var(--hr-inset)] text-[var(--hr-muted-2)]' : tone === 'amber' ? 'bg-[var(--hr-amber-bg)] text-[var(--hr-amber)]' : tone === 'red' ? 'bg-[var(--hr-red-bg)] text-[var(--hr-red)]' : 'bg-[var(--hr-blue-bg)] text-[var(--hr-navy)]')} aria-hidden="true">
          <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
        </span>
        <span className="min-w-0 flex-1">
          <span className={cn('block truncate text-sm font-medium leading-5', zero ? 'text-[var(--hr-muted)]' : 'text-[var(--hr-heading)]')}>{label}</span>
          {meta && <span className="block truncate text-[13px] leading-[18px] text-[var(--hr-muted)]">{meta}</span>}
        </span>
        <span className={cn('hr-num shrink-0 text-[20px] font-semibold leading-6', zero ? 'text-[var(--hr-muted-2)]' : tone === 'amber' ? 'text-[var(--hr-amber)]' : tone === 'red' ? 'text-[var(--hr-red)]' : 'text-[var(--hr-heading)]')} data-testid={testId ? `${testId}-value` : undefined}>
          {fmtNumber(value ?? 0, 0)}
        </span>
        <ChevronRight className="h-4 w-4 shrink-0 text-[var(--hr-muted-2)] transition-transform duration-150 group-hover:translate-x-0.5" aria-hidden="true" />
      </Link>
    </li>
  );
}

function SectionLoading({ rows = 3, testId }) {
  return (
    <div className="space-y-3 px-4 py-4 sm:px-5" aria-busy="true" data-testid={testId}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-9 w-9 rounded-[10px]" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-2/3" />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

function SectionError({ what, onRetry, testId }) {
  return (
    <div className="px-4 py-4 sm:px-5">
      <Note tone="amber" data-testid={testId}>
        Kunne ikke hente {what}.{' '}
        <button type="button" onClick={onRetry} className="font-medium underline underline-offset-2 hover:text-[var(--hr-heading)]" data-testid={testId ? `${testId}-retry` : undefined}>
          Prøv igjen
        </button>
      </Note>
    </div>
  );
}

function FooterLink({ to, children, testId }) {
  return (
    <div className="border-t border-[var(--hr-hairline)] px-4 py-2 sm:px-5">
      <Link to={to} className="inline-flex h-10 items-center gap-1 rounded-[8px] px-1 text-sm font-medium text-[var(--hr-heading)] underline-offset-[3px] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]" data-testid={testId}>
        {children} <ChevronRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Seksjoner                                                            */
/* ------------------------------------------------------------------ */

function MinDagCard({ employee, own, status, onRetry, today, compact }) {
  const entries = own?.entries ?? [];
  const todays = entries.filter((e) => e.date === today);
  const todayHours = todays.reduce((a, e) => a + e.hours, 0);
  const sums = own?.sums;
  const returned = sums?.byStatus?.returnert ?? 0;
  const drafts = sums?.byStatus?.utkast ?? 0;
  return (
    <Panel data-testid="oversikt-min-dag-card">
      <PanelHeader
        eyebrow="Min dag"
        title={employee ? `${employee.name} · ${formatDateWeekday(today)}` : formatDateWeekday(today)}
        description={employee ? `${employee.role} · demoprofil, ikke innlogging` : 'Velg demoprofil for å se egne føringer.'}
        actions={
          <Button asChild variant="outline" size="sm">
            <Link to="/min-dag" data-testid="oversikt-min-dag-open-link">
              Åpne Min dag <ArrowRight className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          </Button>
        }
      />
      {status === 'loading' && <SectionLoading rows={2} testId="oversikt-min-dag-loading" />}
      {status === 'error' && <SectionError what="føringene dine" onRetry={onRetry} testId="oversikt-min-dag-error" />}
      {status === 'ready' && (
        <div className="px-4 py-4 sm:px-5">
          <div className={cn('grid gap-4', compact ? 'sm:grid-cols-3' : 'sm:grid-cols-2')}>
            <div className="min-w-0">
              <div className="text-[13px] leading-[18px] text-[var(--hr-muted)]">I dag</div>
              <div className="hr-num mt-0.5 text-[22px] font-semibold leading-7 text-[var(--hr-heading)]" data-testid="oversikt-min-dag-today">
                {todays.length ? hoursText(todayHours) : '–'}
              </div>
              <div className="text-[13px] leading-[18px] text-[var(--hr-muted-2)]">{todays.length ? `${todays.length} ${todays.length === 1 ? 'føring' : 'føringer'}` : 'Ingen føringer ennå'}</div>
            </div>
            <div className="min-w-0">
              <div className="text-[13px] leading-[18px] text-[var(--hr-muted)]">Denne uka</div>
              <div className="hr-num mt-0.5 text-[22px] font-semibold leading-7 text-[var(--hr-heading)]" data-testid="oversikt-min-dag-week">
                {sums ? hoursText(sums.hours) : '–'}
              </div>
              <div className="text-[13px] leading-[18px] text-[var(--hr-muted-2)]">{sums?.machineHours > 0 ? `${hoursText(sums.machineHours)} maskintimer` : `${entries.length} ${entries.length === 1 ? 'føring' : 'føringer'}`}</div>
            </div>
            <div className={cn('flex flex-wrap items-start gap-1.5 self-start', !compact && 'sm:col-span-2')} data-testid="oversikt-min-dag-statuses">
              {TIME_STATUSES.map((s) => {
                const n = sums?.byStatus?.[s] ?? 0;
                if (!n) return null;
                return (
                  <StatusPill key={s} tone={TIME_STATUS_TONE[s]} data-testid={`oversikt-min-dag-status-${s}`}>
                    <span className="hr-num">{n}</span> {TIME_STATUS_LABELS[s].toLowerCase()}
                  </StatusPill>
                );
              })}
              {!entries.length && <span className="text-[13px] text-[var(--hr-muted-2)]">Ingen føringer denne uka.</span>}
            </div>
          </div>
          {(returned > 0 || drafts > 0) && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {returned > 0 && (
                <Button asChild variant="outline" size="sm" className="border-[rgba(154,91,0,0.35)] text-[var(--hr-amber)] hover:bg-[var(--hr-amber-bg)]">
                  <Link to="/min-dag" data-testid="oversikt-min-dag-returned-link">
                    <RotateCcw className="mr-1.5 h-4 w-4" aria-hidden="true" /> {returned} returnert – rett og send på nytt
                  </Link>
                </Button>
              )}
              {drafts > 0 && (
                <Button asChild variant="outline" size="sm">
                  <Link to="/min-dag" data-testid="oversikt-min-dag-drafts-link">
                    {drafts} {drafts === 1 ? 'utkast' : 'utkast'} ikke sendt
                  </Link>
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}

function TodoPanel({ mode, all, own, machines, events, deviations, status, onRetry, since, today }) {
  const rows = [];
  const range = `from=${since}&to=${today}&periode=egen`;
  if (mode !== 'medarbeider') {
    rows.push({ id: 'pending', icon: ClipboardCheck, label: 'Timer til godkjenning', value: all?.sums?.byStatus?.sendt ?? 0, meta: 'Sendt inn av medarbeiderne · siste 35 dager', to: `/timer?status=sendt&${range}` });
    rows.push({ id: 'returned-all', icon: RotateCcw, label: 'Returnert – venter på rettelse', value: all?.sums?.byStatus?.returnert ?? 0, meta: 'Sendt tilbake med beskjed', to: `/timer?status=returnert&${range}`, tone: 'amber' });
  }
  if (mode !== 'kontor') {
    rows.push({ id: 'own-returned', icon: RotateCcw, label: 'Returnert til deg', value: own?.sums?.byStatus?.returnert ?? 0, meta: 'Rett og send på nytt fra Min dag', to: '/min-dag', tone: 'amber' });
    rows.push({ id: 'own-drafts', icon: CalendarCheck2, label: 'Dine utkast denne uka', value: own?.sums?.byStatus?.utkast ?? 0, meta: 'Ikke sendt til kontoret ennå', to: '/min-dag' });
  }
  const ms = machines?.summary;
  const es = events?.summary;
  const ds = deviations?.summary;
  rows.push({ id: 'service', icon: Wrench, label: 'Service forfalt', value: ms?.forfalt ?? 0, meta: ms ? `${ms.snart ?? 0} snart · ${ms.ukjent ?? 0} uten grunnlag` : undefined, to: '/maskiner?fane=maskinpark', tone: 'amber' });
  rows.push({ id: 'faults', icon: Truck, label: 'Åpne maskinfeil', value: es?.openFaults ?? 0, meta: es?.blocked ? `${es.blocked} ${es.blocked === 1 ? 'maskin kan ikke brukes' : 'maskiner kan ikke brukes'}` : 'Ingen maskiner er sperret', to: '/maskiner?fane=maskinpark', tone: es?.blocked ? 'red' : undefined });
  rows.push({ id: 'ruh', icon: ShieldAlert, label: 'Åpne RUH', value: ds?.openByKind?.ruh ?? 0, meta: ds?.overdue ? `${ds.overdue} med passert frist` : 'Uønskede hendelser og nestenulykker', to: '/avvik?fane=hms&kind=ruh', tone: ds?.overdue ? 'amber' : undefined });
  rows.push({ id: 'kvalitet', icon: ShieldAlert, label: 'Åpne kvalitetsavvik', value: ds?.openByKind?.kvalitet ?? 0, meta: ds?.actionsOpen ? `${ds.actionsOpen} åpne tiltak` : 'Produkt- og leveransekvalitet', to: '/avvik?fane=hms&kind=kvalitet' });

  const total = rows.reduce((a, r) => a + (r.value || 0), 0);
  return (
    <Panel data-testid="oversikt-todo-panel">
      <PanelHeader title="Til oppfølging" description={status === 'ready' ? (total ? `${total} ${total === 1 ? 'punkt' : 'punkter'} som venter · trykk en rad for å gå rett til arbeidet` : 'Ingenting venter akkurat nå.') : 'Henter oppgaver …'} />
      {status === 'loading' && <SectionLoading rows={5} testId="oversikt-todo-loading" />}
      {status === 'error' && <SectionError what="oppfølgingspunktene" onRetry={onRetry} testId="oversikt-todo-error" />}
      {status === 'ready' && (
        <ul className="divide-y divide-[var(--hr-hairline)]" data-testid="oversikt-todo-list">
          {rows.map((r) => (
            <TodoRow key={r.id} icon={r.icon} label={r.label} value={r.value} meta={r.meta} to={r.to} tone={r.tone} testId={`oversikt-todo-${r.id}`} />
          ))}
        </ul>
      )}
    </Panel>
  );
}

function ProjectActivityPanel({ projects, status, onRetry }) {
  const rows = useMemo(() => {
    const list = (projects?.projects ?? []).filter((p) => p.status !== 'avsluttet' && p.active !== false);
    return list.sort((a, b) => (b.lastActivity ?? '').localeCompare(a.lastActivity ?? '')).slice(0, 5);
  }, [projects]);
  return (
    <Panel data-testid="oversikt-projects-panel">
      <PanelHeader title="Prosjektaktivitet" description="Siste 30 dager · timer, maskintimer og vareforbruk per prosjekt (kunde alltid synlig)." />
      {status === 'loading' && <SectionLoading rows={4} testId="oversikt-projects-loading" />}
      {status === 'error' && <SectionError what="prosjektene" onRetry={onRetry} testId="oversikt-projects-error" />}
      {status === 'ready' && rows.length === 0 && <p className="px-4 py-6 text-sm text-[var(--hr-muted)] sm:px-5">Ingen aktive prosjekter med aktivitet i perioden.</p>}
      {status === 'ready' && rows.length > 0 && (
        <ul className="divide-y divide-[var(--hr-hairline)]" data-testid="oversikt-projects-list">
          {rows.map((p) => (
            <li key={p.id}>
              <Link to={`/prosjekter/${encodeURIComponent(p.id)}`} className="group flex min-h-[56px] items-center gap-3 px-4 py-2.5 transition-colors duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--hr-focus)] sm:px-5" data-testid={`oversikt-project-${p.id}`}>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] leading-[18px] text-[var(--hr-muted)]">{p.customer}</span>
                  <span className="block truncate text-sm font-semibold leading-5 text-[var(--hr-heading)]">{p.name}</span>
                  <span className="hr-tabular block truncate text-[13px] leading-[18px] text-[var(--hr-muted)]">
                    {hoursText(p.period.hours)}{p.period.machineHours ? ` · ${hoursText(p.period.machineHours)} maskin` : ''}{p.period.consumptionCount ? ` · ${p.period.consumptionCount} vareforbruk` : ''}
                  </span>
                </span>
                <span className="shrink-0 text-right">
                  {p.period.pending > 0 && <span className="hr-num block text-sm font-semibold leading-5 text-[var(--hr-amber)]">{p.period.pending} til godkjenning</span>}
                  <span className="hr-tabular block text-[13px] leading-[18px] text-[var(--hr-muted-2)]">{p.lastActivity ? `sist ${formatDateShort(p.lastActivity)}` : 'ingen aktivitet'}</span>
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-[var(--hr-muted-2)] transition-transform duration-150 group-hover:translate-x-0.5" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      )}
      <FooterLink to="/prosjekter" testId="oversikt-projects-all-link">Alle prosjekter</FooterLink>
    </Panel>
  );
}

function ConsumptionPanel({ consumption, status, onRetry }) {
  const rows = useMemo(() => [...(consumption?.entries ?? [])].sort((a, b) => (b.date ?? '').localeCompare(a.date ?? '')).slice(0, 3), [consumption]);
  const lineText = (c) => {
    const byUnit = {};
    for (const l of c.lines ?? []) byUnit[l.unit] = (byUnit[l.unit] ?? 0) + (l.quantity ?? 0);
    const first = (c.lines ?? []).slice(0, 2).map((l) => l.item?.name ?? l.itemId).join(', ');
    const more = (c.lines?.length ?? 0) - 2;
    return `${first}${more > 0 ? ` +${more}` : ''}${byUnitText(byUnit) ? ` · ${byUnitText(byUnit)}` : ''}`;
  };
  return (
    <Panel data-testid="oversikt-consumption-panel">
      <PanelHeader
        title="Vareforbruk – nylig"
        description="Siste registreringer med kunde og prosjekt."
        actions={
          <Button asChild size="sm" variant="outline">
            <Link to="/vareforbruk?ny=1" data-testid="oversikt-consumption-new-link">
              <Plus className="mr-1 h-4 w-4" aria-hidden="true" /> Registrer
            </Link>
          </Button>
        }
      />
      {status === 'loading' && <SectionLoading rows={3} testId="oversikt-consumption-loading" />}
      {status === 'error' && <SectionError what="vareforbruket" onRetry={onRetry} testId="oversikt-consumption-error" />}
      {status === 'ready' && rows.length === 0 && <p className="px-4 py-6 text-sm text-[var(--hr-muted)] sm:px-5">Ingen vareforbruk registrert de siste 30 dagene.</p>}
      {status === 'ready' && rows.length > 0 && (
        <ul className="divide-y divide-[var(--hr-hairline)]" data-testid="oversikt-consumption-list">
          {rows.map((c) => (
            <li key={c.id}>
              <Link to={`/vareforbruk?rediger=${encodeURIComponent(c.id)}`} className="group flex min-h-[56px] items-start gap-3 px-4 py-3 transition-colors duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--hr-focus)] sm:px-5" data-testid={`oversikt-consumption-${c.id}`}>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[var(--hr-inset)] text-[var(--hr-muted)]" aria-hidden="true">
                  <Package className="h-[18px] w-[18px]" strokeWidth={1.75} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="hr-tabular block text-[13px] leading-[18px] text-[var(--hr-muted)]">{formatDateShort(c.date)} · {c.employee?.name ?? 'Ukjent'}</span>
                  <span className="block truncate text-sm font-semibold leading-5 text-[var(--hr-heading)]">{c.project?.customer ? `${c.project.customer} · ` : ''}{c.project?.name ?? c.projectId}</span>
                  <span className="hr-tabular block truncate text-[13px] leading-[18px] text-[var(--hr-text)]">{lineText(c)}</span>
                </span>
                <ChevronRight className="mt-2 h-4 w-4 shrink-0 text-[var(--hr-muted-2)]" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      )}
      <FooterLink to="/vareforbruk" testId="oversikt-consumption-all-link">Alt vareforbruk</FooterLink>
    </Panel>
  );
}

function MachinesPanel({ machines, events, status, onRetry }) {
  const { openCase } = useCaseParam();
  const list = machines?.machines ?? [];
  const count = list.filter((m) => m.needsFollowUp).length;
  const blocked = list.filter((m) => m.operational?.status === 'stopp').length;
  return (
    <Panel data-testid="overview-attention-panel">
      <PanelHeader
        title="Maskiner som trenger oppfølging"
        description={status === 'ready' ? `${count} av ${list.length} maskiner${blocked ? ` · ${blocked} kan ikke brukes` : ''} · feil, frigivelse, frister og service` : 'Feil, frigivelse, frister og service.'}
        actions={
          <Button asChild variant="outline" size="sm">
            <Link to="/maskiner?fane=maskinpark" data-testid="overview-attention-all-link">
              Maskinpark <ArrowUpRight className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          </Button>
        }
      />
      {status === 'loading' && <SectionLoading rows={3} testId="oversikt-machines-loading" />}
      {status === 'error' && <SectionError what="maskinregisteret" onRetry={onRetry} testId="oversikt-machines-error" />}
      {status === 'ready' && <AttentionList machines={list} events={events?.events ?? []} onOpenCase={openCase} limit={4} compact testId="overview-attention" />}
      <CaseSheetHost onChanged={onRetry} />
    </Panel>
  );
}

function DeviationsPanel({ deviations, status, onRetry }) {
  const open = useMemo(() => (deviations?.deviations ?? []).filter((d) => d.open).sort((a, b) => Number(!!b.overdue) - Number(!!a.overdue) || (b.date ?? '').localeCompare(a.date ?? '')), [deviations]);
  const s = deviations?.summary;
  return (
    <Panel data-testid="oversikt-deviations-panel">
      <PanelHeader
        title="RUH og kvalitetsavvik"
        description={status === 'ready' && s ? `${s.open} åpne · ${s.openByKind?.ruh ?? 0} RUH · ${s.openByKind?.kvalitet ?? 0} kvalitet${s.overdue ? ` · ${s.overdue} med passert frist` : ''}` : 'Åpne saker med ansvarlig og frist.'}
        actions={
          <Button asChild variant="outline" size="sm">
            <Link to="/avvik?fane=hms&ny=1" data-testid="oversikt-deviations-new-link">
              <Plus className="mr-1 h-4 w-4" aria-hidden="true" /> Meld
            </Link>
          </Button>
        }
      />
      {status === 'loading' && <SectionLoading rows={3} testId="oversikt-deviations-loading" />}
      {status === 'error' && <SectionError what="avvikene" onRetry={onRetry} testId="oversikt-deviations-error" />}
      {status === 'ready' && <DeviationList deviations={open} limit={4} compact emptyText="Ingen åpne RUH eller kvalitetsavvik." testId="oversikt-dev-list" from={{ label: 'Oversikt', to: '/oversikt' }} />}
      <FooterLink to="/avvik?fane=hms" testId="oversikt-deviations-all-link">Alle avvik</FooterLink>
    </Panel>
  );
}

/* ------------------------------------------------------------------ */
/* Side                                                                 */
/* ------------------------------------------------------------------ */
export default function OversiktPage() {
  const { employee, employeeId } = useDemoProfile();
  const today = osloDateOf(new Date());
  const since = addDays(today, -LOOKBACK_DAYS);
  const monthAgo = addDays(today, -30);
  const weekStart = weekStartOf(today);
  const weekEnd = addDays(weekStart, 6);
  const mode = overviewMode(employee?.demoRole);

  const [all, setAll] = useState({ status: 'loading', data: null });
  const [own, setOwn] = useState({ status: 'loading', data: null });
  const [consumption, setConsumption] = useState({ status: 'loading', data: null });
  const [projects, setProjects] = useState({ status: 'loading', data: null });
  const [machines, setMachines] = useState({ status: 'loading', data: null, events: null });
  const [deviations, setDeviations] = useState({ status: 'loading', data: null });

  const run = useCallback(async (setter, fn) => {
    setter((s) => ({ ...s, status: s.data ? 'ready' : 'loading' }));
    try {
      const data = await fn();
      setter({ status: 'ready', data });
    } catch {
      setter((s) => ({ ...s, status: 'error' }));
    }
  }, []);

  const loadAll = useCallback(() => run(setAll, () => listTimeEntries({ from: since, to: today })), [run, since, today]);
  const loadOwn = useCallback(() => {
    if (!employeeId) return;
    return run(setOwn, () => listTimeEntries({ employeeId, from: weekStart, to: weekEnd }));
  }, [run, employeeId, weekStart, weekEnd]);
  const loadConsumption = useCallback(() => run(setConsumption, () => listConsumption({ from: monthAgo, to: today })), [run, monthAgo, today]);
  const loadProjects = useCallback(() => run(setProjects, () => listProjects({ from: monthAgo, to: today })), [run, monthAgo, today]);
  const loadMachines = useCallback(async () => {
    setMachines((s) => ({ ...s, status: s.data ? 'ready' : 'loading' }));
    try {
      const [m, ev] = await Promise.all([listMachines(), listEvents({ open: 1 })]);
      setMachines({ status: 'ready', data: m, events: ev });
    } catch {
      setMachines((s) => ({ ...s, status: 'error' }));
    }
  }, []);
  const loadDeviations = useCallback(() => run(setDeviations, () => listDeviations({ open: 1 })), [run]);

  useEffect(() => {
    document.title = 'Oversikt · Hardanger Rock';
  }, []);
  useEffect(() => {
    loadAll();
    loadConsumption();
    loadProjects();
    loadMachines();
    loadDeviations();
  }, [loadAll, loadConsumption, loadProjects, loadMachines, loadDeviations]);
  useEffect(() => {
    loadOwn();
  }, [loadOwn]);

  const minDag = <MinDagCard key="min-dag" employee={employee} own={own.data} status={employeeId ? own.status : 'ready'} onRetry={loadOwn} today={today} compact={mode === 'kontor'} />;
  const todo = <TodoPanel key="todo" mode={mode} all={all.data} own={own.data} machines={machines.data} events={machines.events} deviations={deviations.data} status={all.status === 'loading' || (employeeId && own.status === 'loading') ? 'loading' : all.status === 'error' ? 'error' : 'ready'} onRetry={() => { loadAll(); loadOwn(); loadMachines(); loadDeviations(); }} since={since} today={today} />;
  const projectsPanel = <ProjectActivityPanel key="projects" projects={projects.data} status={projects.status} onRetry={loadProjects} />;
  const consumptionPanel = <ConsumptionPanel key="consumption" consumption={consumption.data} status={consumption.status} onRetry={loadConsumption} />;
  const machinesPanel = <MachinesPanel key="machines" machines={machines.data} events={machines.events} status={machines.status} onRetry={loadMachines} />;
  const deviationsPanel = <DeviationsPanel key="deviations" deviations={deviations.data} status={deviations.status} onRetry={loadDeviations} />;

  const left = mode === 'kontor' ? [todo, projectsPanel, minDag] : [minDag, todo, projectsPanel];
  const right = mode === 'medarbeider' ? [consumptionPanel, machinesPanel, deviationsPanel] : [machinesPanel, deviationsPanel, consumptionPanel];

  return (
    <div className="space-y-6" data-testid="oversikt-page" data-mode={mode}>
      <PageHeader title="Oversikt" lead={<span data-testid="oversikt-lead">{formatDateWeekday(today)} · arbeidsstart tilpasset demoprofilen ({MODE_LABEL[mode]})</span>} leadTestId="oversikt-page-lead">
        <DemoProfileSwitcher compact className="mr-1 hidden sm:flex" />
        <Button asChild className="h-11 px-4 lg:h-10" data-testid="oversikt-primary-log-time-button">
          <Link to="/min-dag?ny=1">
            <Plus className="mr-1 h-4 w-4" aria-hidden="true" /> Før timer
          </Link>
        </Button>
      </PageHeader>

      <ExampleDataNote>Rekkefølgen følger demoprofilen – visningskontekst, ikke tilgangsstyring.</ExampleDataNote>

      <div className="grid gap-6 lg:grid-cols-12">
        <div className="min-w-0 space-y-6 lg:col-span-7 xl:col-span-8">{left}</div>
        <div className="min-w-0 space-y-6 lg:col-span-5 xl:col-span-4">{right}</div>
      </div>

      <ProductionAnalysisPanel />
    </div>
  );
}
